/* eslint-disable no-unused-vars */
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import LandingPage from './LandingPage.js';
import ClassicGame from './ClassicGame.js';
import ReelConnectionsGame from './ReelConnectionsGame.js';
import Toolbar from './Toolbar.js';
import Footer from './Footer.js';
import LoadingScreen from './LoadingScreen.js';
import UpdateBanner from './UpdateBanner.js';
import { AuthProvider, useAuth } from './AuthProvider.js';
import './App.css';
import { initGA, trackDarkModeToggle } from './analytics.js';
import { DateTime } from 'luxon';

function App() {
  const [gameMode, setGameMode] = useState('landing');
  const [stats, setStats] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Immediate check to prevent theme flash
    const savedStats = localStorage.getItem('mojie_stats');
    if (savedStats) {
      try {
        return JSON.parse(savedStats).darkMode;
      } catch (e) { }
    }
    return false;
  });
  const [loading, setLoading] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [selectedDate, setSelectedDate] = useState(DateTime.utc().toISODate());
  const [completedQuizDates, setCompletedQuizDates] = useState([]);

  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || '/api';
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  // Stabilize userId to prevent re-render loops
  let cookieUserId = Cookies.get('userId');
  if (!cookieUserId) {
    cookieUserId = uuidv4();
    Cookies.set('userId', cookieUserId, { expires: 365 });
  }

  const userId = React.useMemo(() => {
    return isAuthenticated && user ? user.id : cookieUserId;
  }, [isAuthenticated, user, cookieUserId]);

  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-mode' : 'light-mode';
    initGA();
  }, [isDarkMode]);

  const fetchStats = React.useCallback(async () => {
    if (!userId || authLoading) return;
    try {
      const modeToSlug = {
        'classic': 'emoji-quiz',
        'connections': 'reel-connections'
      };
      const gameId = modeToSlug[gameMode] || null;

      let url = `${apiEndpoint}/stats?userId=${userId}`;
      if (gameId) url += `&gameId=${gameId}`;

      const response = await axios.get(url);
      setStats(response.data);
      // Persist locally for immediate theme on next load
      localStorage.setItem('mojie_stats', JSON.stringify(response.data));

      const dismissed = localStorage.getItem(`howToPlayDismissed_${gameId}`);
      setShowHowToPlay(response?.data?.gamesPlayed === 0 && !dismissed);
      setIsDarkMode(response?.data?.darkMode ?? false);
    } catch (error) {
      console.error('Error fetching stats:', error.message);
    }
  }, [userId, authLoading, gameMode, apiEndpoint]);

  const fetchHistoricalQuizzes = React.useCallback(async () => {
    if (!userId || authLoading) return;
    try {
      const modeToSlug = {
        'classic': 'emoji-quiz',
        'connections': 'reel-connections'
      };
      const gameId = modeToSlug[gameMode] || null;

      let url = `${apiEndpoint}/get-completed-quizzes?userId=${userId}`;
      if (gameId) url += `&gameId=${gameId}`;

      const response = await axios.get(url);
      setCompletedQuizDates(response.data.completedQuizDates);
    } catch (error) {
      console.error('Error fetching historical quizzes:', error.message);
    }
  }, [userId, authLoading, gameMode, apiEndpoint]);

  // DEEP LINKING: Check for game parameter in URL once on load
  const [hasDeepLinked, setHasDeepLinked] = useState(false);
  useEffect(() => {
    if (!userId || authLoading || hasDeepLinked) return;

    const params = new URLSearchParams(window.location.search);
    const gameParam = params.get('game');
    const dateParam = params.get('date');

    if (gameParam === 'classic') {
      setGameMode('classic');
    } else if (gameParam === 'iconnect' || gameParam === 'connections') {
      setGameMode('connections');
    }

    if (dateParam) {
      handleDateChange(dateParam);
    }
    setHasDeepLinked(true);
  }, [userId, authLoading, hasDeepLinked]);

  useEffect(() => {
    if (!userId || authLoading) return;

    // Initial data load - only set global loading for the very first fetch or game switch
    const performInitialLoad = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchHistoricalQuizzes()]);
      setLoading(false);
    };

    performInitialLoad();
  }, [userId, authLoading, gameMode, fetchStats, fetchHistoricalQuizzes]);

  const toggleTheme = () => {
    const nextMode = !isDarkMode;
    setIsDarkMode(nextMode);
    trackDarkModeToggle(nextMode);
  };

  const handleDateChange = (date) => {
    // Ensure date is always normalized to YYYY-MM-DD
    const isoDate = date.includes('/')
      ? DateTime.fromFormat(date, 'MM/dd/yyyy').toISODate()
      : date;
    setSelectedDate(isoDate);
  };

  const handleSelectGame = (mode) => {
    setGameMode(mode);
  };

  const renderGame = () => {
    switch (gameMode) {
      case 'classic':
        return (
          <ClassicGame
            user={user}
            userId={userId}
            isAuthenticated={isAuthenticated}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            stats={stats}
            fetchStats={fetchStats}
            authLoading={authLoading}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            completedQuizDates={completedQuizDates}
            fetchHistoricalQuizzes={fetchHistoricalQuizzes}
          />
        );
      case 'connections':
        return (
          <ReelConnectionsGame
            user={user}
            userId={userId}
            isAuthenticated={isAuthenticated}
            isDarkMode={isDarkMode}
            stats={stats}
            onBack={() => setGameMode('landing')}
            selectedDate={selectedDate}
            fetchStats={fetchStats}
            fetchHistoricalQuizzes={fetchHistoricalQuizzes}
          />
        );
      default:
        return <LandingPage onSelectGame={handleSelectGame} />;
    }
  };

  useEffect(() => {
    const handleFocus = () => {
      const currentUtc = DateTime.utc().toISODate();
      if (currentUtc !== selectedDate) {
        setSelectedDate(currentUtc);
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') handleFocus();
    });
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [selectedDate]);

  return (
    <div className={`app-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <LoadingScreen loading={loading || authLoading} />

      <Toolbar
        isDarkMode={isDarkMode}
        onToggle={toggleTheme}
        stats={stats}
        showHowToPlay={showHowToPlay}
        handleDateChange={handleDateChange}
        completedQuizDates={completedQuizDates}
        date={selectedDate}
        onHome={() => setGameMode('landing')}
        gameMode={gameMode}
      />
      <UpdateBanner />
      <div className="main-content-wrapper">
        {renderGame()}
      </div>
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
}

// Wrap App with AuthProvider
function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;
